/**
 * PDA 前端本地预校验错误构建工具。
 *
 * 枚举定义见 clientError.ts，此文件只含类型 + 工具函数。
 *
 * 使用方式：
 * ```ts
 * // 无参数
 * scanPromptRef.current.showResult(
 *     buildClientError(ClientError.SORTING_PLEASE_SELECT_DESTINATION), barCode
 * );
 * // 带占位符参数
 * scanPromptRef.current.showResult(
 *     buildClientError(ClientError.BAG_MERGE_TOTAL_EXCEED_LIMIT, { totalCount: 100, limit: 3000 }), barCode
 * );
 * // 带 title
 * scanPromptRef.current.showResult(
 *     buildClientError(ClientError.INVALID_TRACKING_NUMBER, { barCode }, ClientError.SORTING_SCAN_TITLE), barCode
 * );
 * ```
 */

import i18n from '../../../i18n';
import type { ScanPromptInfo, ScanResultEnvelope } from './types';

/** 单条客户端错误定义，供 clientError.ts 使用 */
export interface ClientErrorDef {
    readonly messageKey: string;
    /** i18n_config 表的 message_key，与后端渲染路径对齐；同时作为前端 i18next 取文案的 key */
    readonly i18n_key?: string;
    readonly popupType: ScanPromptInfo['popupType'];
}

/**
 * 从任意 ScanResultEnvelope 提取失败列表展示用的 failedReason。
 * content 优先（具体描述），无则 fallback 到 title，再 fallback 到 message。
 */
export function getEnvelopeFailedReason(env: ScanResultEnvelope): string {
    const pi = (env.data as { promptInfo?: ScanPromptInfo } | null | undefined)?.promptInfo;
    return pi?.content || pi?.title || env.message || '';
}

/**
 * 构建前端本地错误的 ScanResultEnvelope，可直接传给 showResult。
 *
 * - 通过 i18next 按当前语言自动取文案，支持 {{param}} 占位符
 * - promptInfo 放在 data 内，与 useScanResultPrompt.toPrompt 读取路径一致
 *
 * @param error      ClientError 中的错误定义
 * @param params     占位符参数，如 { barCode: 'TT001' }
 * @param titleEntry 标题枚举，传 ClientError.ARRIVAL_SCAN_TITLE 等；不传则无标题
 */
export function buildClientError(
    error: ClientErrorDef,
    params?: Record<string, string | number>,
    titleEntry?: ClientErrorDef,
): ScanResultEnvelope<{ promptInfo: ScanPromptInfo }> {
    // titleEntry 显式传入时优先用它作为 title（适合网络错误等通用错误复用各扫描页入口 title）；
    // title 类 entry 的 i18n_key 是平铺字符串（如 pda.prompt.title.arrival → "入库失败"），直接读；
    // 否则从 error.i18n_key.title 取（error entry 的翻译是 { title, content } 对象）；再否则无标题
    const title = titleEntry?.i18n_key
        ? i18n.t(titleEntry.i18n_key)
        : (error.i18n_key ? i18n.t(`${error.i18n_key}.title`) : '');
    // 从 i18n_key.content 取正文，支持 {{param}} 占位符
    const content = error.i18n_key
        ? i18n.t(`${error.i18n_key}.content`, params as Record<string, string>)
        : '';
    const promptInfo: ScanPromptInfo = {
        messageKey: error.messageKey,
        popupType: error.popupType,
        title,
        content,
    };
    return {
        success: false,
        data: { promptInfo },
    };
}
